import React from 'react';
import Select from 'react-select';
import { UserUIModel } from '../../../../../shared/ui-models/user.ui-model';
import {
  CHECKBOX_LABEL,
  FIELD,
  LABEL,
  SECTION_TITLE,
  selectProps
} from '../../reservation-create.ui';

interface SalesPersonOption {
  value: string;
  label: string;
  salesPerson: UserUIModel;
}

export interface OptionsValues {
  salesPerson: UserUIModel | null;
  isRelatedReservation: boolean;
  relatedReservationId: string;
  skipEmail: boolean;
  isComplimentary: boolean;
}

export interface OptionsSectionProps {
  data: {
    values: OptionsValues;
    salesPersons: UserUIModel[];
    isSalesPersonsLoading: boolean;
    isSalesPersonRequired: boolean;
  };
  eventHandlers: {
    onChange: (values: OptionsValues) => void;
  };
}

const toOption = (salesPerson: UserUIModel): SalesPersonOption => ({
  value: salesPerson.id || '',
  label: salesPerson.name || salesPerson.email || salesPerson.phone || 'Unnamed sales person',
  salesPerson
});

export const OptionsSection: React.FC<OptionsSectionProps> = ({
  data: { values, salesPersons, isSalesPersonsLoading, isSalesPersonRequired },
  eventHandlers: { onChange }
}) => (
  <>
    <h2 className={SECTION_TITLE}>Options</h2>
    <div className="flex flex-col gap-[20px]">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-[12px]">
        <label className={LABEL}>
          Sales person
          <Select<SalesPersonOption, false>
            {...selectProps}
            options={salesPersons.map(toOption)}
            value={values.salesPerson ? toOption(values.salesPerson) : null}
            onChange={(value) => onChange({ ...values, salesPerson: value?.salesPerson || null })}
            placeholder="Select sales person"
            isLoading={isSalesPersonsLoading}
            isDisabled={isSalesPersonsLoading}
            isClearable
            isSearchable
          />
          {isSalesPersonRequired && !values.salesPerson && (
            <span className="text-[12px] text-red-500">Required for this source</span>
          )}
        </label>
        {values.isRelatedReservation && (
          <label className={LABEL}>
            Related reservation number
            <input
              className={FIELD}
              type="number"
              autoFocus
              value={values.relatedReservationId}
              onChange={(event) =>
                onChange({ ...values, relatedReservationId: event.target.value })
              }
            />
          </label>
        )}
      </div>
      <div className="flex flex-col gap-[12px]">
        <label className={CHECKBOX_LABEL}>
          <input
            type="checkbox"
            className="size-[16px] accent-primary-500"
            checked={values.isRelatedReservation}
            onChange={(event) =>
              onChange({
                ...values,
                isRelatedReservation: event.target.checked,
                relatedReservationId: event.target.checked ? values.relatedReservationId : ''
              })
            }
          />
          Linked to another reservation
        </label>
        <label className={CHECKBOX_LABEL}>
          <input
            type="checkbox"
            className="size-[16px] accent-primary-500"
            checked={values.skipEmail}
            onChange={(event) => onChange({ ...values, skipEmail: event.target.checked })}
          />
          Don't send confirmation email to guest
        </label>
        <label className={CHECKBOX_LABEL}>
          <button
            type="button"
            role="switch"
            aria-checked={values.isComplimentary}
            onClick={() => onChange({ ...values, isComplimentary: !values.isComplimentary })}
            className={`relative inline-flex h-[20px] w-[36px] shrink-0 items-center rounded-full transition-colors ${
              values.isComplimentary ? 'bg-primary-500' : 'bg-gray-300'
            }`}
          >
            <span
              className={`inline-block size-[16px] rounded-full bg-white shadow transition-transform ${
                values.isComplimentary ? 'translate-x-[18px]' : 'translate-x-[2px]'
              }`}
            />
          </button>
          Complimentary
        </label>
      </div>
    </div>
  </>
);
